import { useAuth } from '../../../utils/Auth/AuthProvider';
import { PanelCard, Chip, EmptyState } from '../../components/ui';
import Spinner from '../../components/ui/Spinner';
import ErrorPanel from '../../components/ui/ErrorPanel';
import { FaChartLine } from '../../components/ui/icons';
import { useClassPerfBySubject, useTeacherAssignments } from '../../api/hooks';
import { pct } from '../dashboards/sections/format';
import { Key, Legend, shortSection, signed1, deltaTone } from '../class_performance/lib/cpBits';
import { subjectTeachers } from './sectionMath';

const WEAK_BELOW = 55;

interface Props { gradeId: number; sectionId: number; gradeName?: string; sectionName?: string }

export default function SectionSubjects({ gradeId, sectionId, gradeName, sectionName }: Props) {
  const sec = useClassPerfBySubject({ grade: gradeId, section: sectionId });
  const grade = useClassPerfBySubject({ grade: gradeId, section: 'all' });
  const { role } = useAuth();
  const assignments = useTeacherAssignments({ enabled: role === 'school_admin' || role === 'school' || role === 'admin', retry: false });
  const short = shortSection(gradeName, sectionName) || 'Section';
  const gradeLabel = gradeName ?? 'Grade';
  // Teacher under each subject name; the assignments endpoint is admin-only, so it may simply be absent.
  const teacherOf = new Map(
    (assignments.data ? subjectTeachers(assignments.data, gradeId, sectionId) : []).map((t) => [t.subjectId, t.name]),
  );
  let body;
  if (sec.isLoading) body = <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  else if (sec.isError || !sec.data) body = <ErrorPanel />;
  else {
    const rows = sec.data.subjects.filter((s) => s.avg_score != null)
      .sort((a, b) => a.avg_score! - b.avg_score!);
    const gradeAvg = new Map((grade.data?.subjects ?? []).map((s) => [s.id, s.avg_score]));
    const note = grade.isError ? <p className="mb-3 text-xs text-muted">Grade comparison unavailable</p> : null;
    body = rows.length === 0 ? (
      <EmptyState compact icon={<FaChartLine />} title="No subject data yet" body="Subjects appear once this section has exam attempts." />
    ) : (
      <>{note}<ul>
        {rows.map((s) => {
          const g = gradeAvg.get(s.id) ?? null;
          const d = g != null ? s.avg_score! - g : null;
          return (
            <li key={s.id} className="grid grid-cols-[minmax(0,1fr)_54px_70px] items-center gap-x-3.5 gap-y-2 border-b border-hairline py-3 last:border-b-0 min-[641px]:grid-cols-[minmax(120px,1.1fr)_minmax(120px,2fr)_54px_70px]">
              <span className="min-w-0">
                <span className="block font-semibold">{s.name}</span>
                {teacherOf.get(s.id) && <span className="text-xs text-muted">{teacherOf.get(s.id)}</span>}
              </span>
              <div className="relative order-last col-span-3 h-2.5 rounded-[5px] bg-track min-[641px]:order-none min-[641px]:col-span-1">
                <div role="progressbar" aria-label={`${s.name} section average`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(s.avg_score!)}
                  className={`h-full rounded-[5px] ${s.avg_score! < WEAK_BELOW ? 'bg-accent' : 'bg-slate'}`} style={{ width: `${Math.max(0, Math.min(100, s.avg_score!))}%` }} />
                {g != null && (
                  <span role="img" aria-label={`${s.name} grade average ${pct(g)}`} title={`${gradeLabel}: ${pct(g)}`}
                    className="absolute -top-1 -ml-px h-[18px] w-[3px] rounded-sm bg-navy dark:bg-mist-fixed" style={{ left: `${Math.max(0, Math.min(100, g))}%` }} />
                )}
              </div>
              <span className="text-right font-mono font-semibold tabular-nums">{pct(s.avg_score)}</span>
              <span className="text-right">{d != null && <Chip tone={deltaTone(d)}>{signed1(d)}</Chip>}</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-3.5"><Legend>
        <Key tone="slate">{short}</Key>
        <Key tone="accent">{`${short} under ${WEAK_BELOW}`}</Key>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="inline-block h-3 w-[3px] rounded-sm bg-navy dark:bg-mist-fixed" />{gradeLabel}</span>
      </Legend></div></>
    );
  }
  return (
    <PanelCard title="Subject breakdown" align="end" className="h-full" aria-label="Subject breakdown"
      subtitle={`Bar is ${short}, tick is the ${gradeLabel} figure · weakest first`}>
      {body}
    </PanelCard>
  );
}
